<template>
    <div>
        <label v-if="label" class="label">{{ label }}</label>

        <p class="control" :class="{ 'has-icons-right': populated }">
            <e-autocomplete
                ref="autocomplete"
                v-model="typed"
                :data="data"
                :placeholder="placeholder"
                :loading="isFetching"
                :keep-first="keepFirst"
                :open-on-focus="openOnFocus"
                :field="field"
                :icon="includeIcon === '' ? '' : includeIcon"
                clearable
                @typing="getAsyncData"
                @select="select"
                @keyup.esc="clear"
                @clear="clear"
                @focus="$emit('focus')"
                @blur="$emit('blur')"
            >
                <template #default="props">
                    <slot :item-data="props" name="item" />
                </template>

                <template v-if="canAdd && !showAdd" #empty>
                    <!--                    <a onclick="$emit('addNew', typed)">Add new...</a>-->
                    <a @click="addNew()">Add new...</a>
                </template>

                <template v-if="showAdd" #footer>
                    <a @click="addNew()">Add new...</a>
                </template>
            </e-autocomplete>
        </p>

        <p v-if="help" class="help has-text-subtle" :class="{'is-danger': error}">{{ help }}</p>
    </div>
</template>

<script>
import debounce from 'lodash/debounce.js';
import get from 'lodash/get.js';

import Course from '@/models/Course.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'SearchAutocomplete',

    props: {
        label: {
            type: String,
            required: false,
            default: '',
        },

        help: {
            type: String,
            required: false,
            default: '',
        },

        error: {
            type: Boolean,
            required: false,
            default: false,
        },

        placeholder: {
            type: String,
            required: false,
            default: '',
        },

        field: {
            type: String,
            required: true,
        },

        keepFirst: {
            type: Boolean,
            required: false,
            default: false,
        },

        openOnFocus: {
            type: Boolean,
            required: false,
            default: false,
        },

        apiEndpoint: {
            type: Function,
            required: true,
        },

        initialData: {
            type: Array,
            required: false,
            default: () => [],
        },

        setValue: {
            type: String,
            required: false,
            default: null,
        },

        modelValue: {
            type: Object,
            required: false,
            default: null,
        },

        filterType: {
            type: String,
            required: false,
            default: null,
        },

        canAdd: {
            type: Boolean,
            required: false,
            default: false,
        },

        showAdd: {
            type: Boolean,
            required: false,
            default: false,
        },

        urlParams: {
            type: String,
            required: false,
            default: null,
        },

        includeIcon: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['focus', 'blur', 'selected', 'clear', 'add-new', 'esc'],

    data: () => ({
        data: [],
        typed: null,
        selected: null,
        isFetching: false,
    }),

    computed: {
        populated() {
            return (!this.isFetching && this.typed !== null) || this.selected !== null;
        },

        course() {
            return Course.find(this.$route.params.courseId);
        },
    },

    watch: {
        initialData(initialData) {
            this.data = initialData;
        },

        setValue(event) {
            this.typed = event;
        },

        modelValue(modelValue) {
            if (modelValue) {
                this.data = [modelValue];
                this.select(modelValue);
                this.typed = get(modelValue, this.field);
            }
        },
    },

    created() {
        this.data = this.initialData;

        this.typed = this.setValue;
    },

    mounted() {
        if (this.filterType === 'Course Codes' && this.course) {
            // this.$refs.autocomplete.setSelected(this.course.course_codes[0]);
        }

        if (this.urlParams) {
            this.syncFromUrlParams();
        }
    },

    methods: {
        /**
         * Do not use an arrow function here to preserve `this` scope inside `debounce`.
         */
        getAsyncData: debounce(async function getAsyncData(search) {
            if (search === '') {
                return;
            }

            this.data = [];

            this.isFetching = true;

            try {
                const { data } = await axios.get(this.apiEndpoint(search));

                this.data = [];

                data.data.forEach(item => this.data.push(item));
            } catch (error) {
                this.data = [];
                throw error;
            } finally {
                this.isFetching = false;
            }
        }, 500),

        select(option) {
            this.$emit('selected', option);

            this.selected = option;
        },

        clear() {
            this.$emit('clear');
            this.selected = null;
            this.typed = null;
            this.data = this.initialData;
        },

        addNew() {
            this.$emit('add-new', this.typed);
        },

        async syncFromUrlParams() {
            const parameter = new URL(window.location.href).searchParams.get(this.urlParams);

            if (parameter) {
                const url = this.createUrlFromParams(parameter);

                try {
                    const { data } = await axios.get(url);

                    this.typed = this.createTypedFromData(data, parameter);
                } catch (error) {
                    this.data = [];
                    throw error;
                }
            }
        },

        createUrlFromParams(parameter) {
            let url = '';

            if (this.urlParams === 'instructor') {
                url = `/api/users/${parameter}`;
            }

            if (this.urlParams === 'groups') {
                url = `/api/groups/${parameter}`;
            }

            if (this.urlParams === 'tags') {
                url = `/api/tags?search=${parameter}&type=course`;
            }

            if (this.urlParams === 'course_code') {
                url = `/api/course-codes/${parameter}`;
            }
            return url;
        },

        createTypedFromData(data, paramater) {
            if (this.urlParams === 'instructor' || this.urlParams === 'groups') {
                return data.data.name;
            }

            if (this.urlParams === 'tags') {
                return paramater;
            }

            if (this.urlParams === 'course_code') {
                return data.data[0].name;
            }

            return null;
        },
    },
};
</script>
